import React, { useState, useEffect } from 'react';
import withoutAuth from '../../hoc/withoutAuth';
import {
	Box,
	Button,
	Text,
	InputPassword,
	CpfInput,
	InputText,
	Spinner,
} from 'condolivre-ds';
import { Layout } from '../../components/Layout/Layout';
import { Container, Row, Col } from 'react-grid-system';
import styles from './index.module.scss';
import Image from 'next/image';
import { formModel, validateModel } from './model';
import withApollo from '../../hoc/withApollo';
import { useAuth } from '../../providers/Auth';
import {
	useLoginMutation,
	useForgotPasswordMutation,
	useCheckDocumentLazyQuery,
} from '../../../generated';
import { registerCookie } from '../../helpers/index';
import { useRouter } from 'next/router';
import { gtmShootEvent, tagScreenEnum } from '../../helpers/tag';
import { usePartner } from '../../providers/Partner';
export default withoutAuth(
	withApollo(function Login() {
		const [responseStatus, setResponseStatus] = useState('');
		const [responseMsg, setResponseMsg] = useState('');
		const [responseEmail, setResponseEmail] = useState('');
		const [responsePhone, setResponsePhone] = useState('');
		const [form, setForm] = useState(formModel);
		const [validateRules, setValidateRules] = useState(validateModel);
		const [validateReady, setValidateReady] = useState(false);
		const [validateForgotReady, setValidateForgotReady] = useState(false);
		const { setAuthenticated } = useAuth();

		const [userData, setUserData] = useState({ token: '', data: '' });

		const router = useRouter();
		const { cpf } = router.query;

		const status = {
			success: 'success',
			error: 'error',
			reset: 'reset',
		};

		const { logo, name, pathname: partner_pathname } = usePartner();

		useEffect(() => {
			if (cpf) {
				setForm({ ...form, cpf: cpf!.toString() });
			}
		}, [cpf]);

		useEffect(() => {
			if (validateReady) {
				validate();
			}
			if (validateForgotReady) {
				validateForgot();
			}
		}, [form]);

		useEffect(() => {
			setResponseEmail(
				responseEmail.replace(responseEmail.substring(4, 11), '*******'),
			);
		}, [responseEmail]);

		const validate = () => {
			setValidateReady(true);
			setValidateRules({
				cpf: form.cpf ? '' : 'Campo obrigatório',
				password: form.password ? '' : 'Campo obrigatório',
			});
		};

		const validateForgot = () => {
			setValidateForgotReady(true);
			setValidateRules({
				cpf: form.cpf ? '' : 'É necessário preencher um cpf.',
				password: '',
			});
		};

		const handleCPF = (field: any) => (e: any) => {
			setForm({
				...form,
				[field]: e.value,
			});
		};

		const handleInput = (field: any) => (e: any) => {
			setForm({
				...form,
				[field]: e.target.value,
			});
		};

		const shootEvent = (id: string) => (e: any) => {
			gtmShootEvent({
				screen: tagScreenEnum.COLLABORATOR_LOGIN,
				label: id,
				section: null,
				payload: null,
			});
		};

		const [handleForgotPassword, { data: dataForgot }] =
			useForgotPasswordMutation({
				onCompleted: response => {
					setResponseEmail(response.forgotPassword.email);
					setResponsePhone(response.forgotPassword.phone);
					setResponseStatus(status.reset);
				},
			});

		const responseStatusCode = {
			not_found: 'NOT_FOUND',
			first_access: 'FIRST_ACCESS',
			active: 'ACTIVE',
			prospect: 'PROSPECT',
			onboard: 'ONBOARD',
		};

		const [refetch, { data: dataCheckDocument }] = useCheckDocumentLazyQuery({
			variables: { cpf: form.cpf },
			onCompleted: response => {
				if (response.checkDocument.status === responseStatusCode.onboard) {
					router.push({
						pathname: '/reset-password',
						query: { cpf: form.cpf },
					});
				} else if (
					response.checkDocument.status === responseStatusCode.active
				) {
					registerCookie(userData.token, new Date(userData.data));
					setAuthenticated(true);

					router.push({
						pathname: '/general',
					});
				}
			},
			onError: function (): void {
				router.push({
					pathname: '/register/not-found',
				});
			},
		});

		const goToLogin = () => {
			router.push({
				pathname: `/login`,
			});
		};

		const [showModal, setShowModal] = useState({
			show: false,
			type: 'warning',
			callToAction: goToLogin,
			message: '',
			loaderAnimation: false,
			loaderImgPath: '',
			loaderMessage: '',
		});

		const closeModal = () => {
			setShowModal({
				...showModal,
				show: false,
			});
		};

		const [handleSubmit, { data, error, loading }] = useLoginMutation({
			onError: error => {
				gtmEndRequest();
				setValidateRules({
					cpf: '',
					password: 'Dados inválidos, tente novamente.',
				});
				setShowModal({
					...showModal,
					show: true,
					callToAction: closeModal,
					message: 'Ocorreu um erro, tente novamente.',
				});
			},
			onCompleted: response => {
				if (response.login.error) {
					setShowModal({
						...showModal,
						show: true,
						callToAction: closeModal,
						message: response.login.error.message,
					});
				}
				if (response.login.data) {
					gtmEndRequest();
					setUserData({
						token: response.login.data.access_token,
						data: response.login.data.expires_in,
					});

					refetch();
				}
			},
		});

		const gtmEndRequest = () => {
			gtmShootEvent({
				screen: tagScreenEnum.COLLABORATOR_LOGIN,
				label: 'btn-acessar-a-plataforma',
				section: null,
				payload: JSON.stringify({ request_end_date: new Date() }),
			});
		};

		const onSubmitKeyDown = e => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				onSubmit();
			}
		};

		const onSubmit = () => {
			gtmShootEvent({
				screen: tagScreenEnum.COLLABORATOR_LOGIN,
				label: 'btn-acessar-a-plataforma',
				section: null,
				payload: JSON.stringify({ request_start_date: new Date() }),
			});

			validate();

			setValidateRules(state => {
				if (!state.cpf && !state.password) {
					handleSubmit({
						variables: { cpf: form.cpf, password: form.password },
					});
				}

				return state;
			});
		};

		const onSubmitForgot = () => {
			gtmShootEvent({
				screen: tagScreenEnum.COLLABORATOR_LOGIN,
				label: 'btn-esqueci-minha-senha',
				section: null,
				payload: null,
			});

			validateForgot();

			setValidateRules(state => {
				if (!state.cpf) {
					handleForgotPassword({
						variables: {
							cpf: form.cpf,
							partner_pathname,
						},
						onCompleted: data => {
							if (!data.forgotPassword.email) {
								setShowModal({
									...showModal,
									show: true,
									message: 'Não foi possível resgatar sua senha',
								});
							}
						},
					});
				}

				return state;
			});
		};

		const onPreRegisterUrl = () => {
			// TODO, PÁGINA AINDA NÃO EXISTE
		};

		const backToLogin = () => {
			gtmShootEvent({
				screen: tagScreenEnum.COLLABORATOR_RESET_PASSWORD,
				label: 'btn-voltar-ao-inicio',
				section: null,
				payload: null,
			});
			setResponseStatus('');
		};

		const renderDefault = () => {
			return (
				<>
					<Box className={styles.boxLogin} data-type="restrain-style">
						<Text color="black" weight="v_500" size="v_26">
							Seja bem-vindo!
						</Text>

						<Text
							color="gray_700"
							family="regular"
							weight="v_500"
							size="v_16"
							className={styles.sub}>
							Insira seus dados para acessar a plataforma
						</Text>

						<CpfInput
							name="cpf"
							id="cpf"
							onClick={shootEvent('txt-cpf')}
							onChange={handleCPF('cpf')}
							error={validateRules.cpf.length > 0}
							errorMessage={validateRules.cpf}
							label="CPF"
							value={form.cpf}
						/>

						<InputPassword
							name="password"
							id="password"
							onClick={shootEvent('txt-password')}
							onChange={handleInput('password')}
							error={validateRules.password.length > 0}
							errorMessage={validateRules.password}
							label="Senha"
							placeholder="Senha"
							onKeyDown={onSubmitKeyDown}
						/>

						<div className={styles.containerForgot} data-type="restrain-style">
							<button
								className={styles.forgot}
								onClick={onSubmitForgot}
								data-type="restrain-style">
								Esqueci minha senha
							</button>
						</div>

						<Button
							size="normal"
							variant="primary"
							data-type="restrain-style"
							onClick={onSubmit}>
							{loading ? (
								<Spinner color="white" size="25px" />
							) : (
								'Acessar a plataforma'
							)}
						</Button>
					</Box>
				</>
			);
		};

		const renderError = () => {
			return (
				<Box
					className={[styles.boxLogin, styles.success].join(' ')}
					data-type="restrain-style">
					<Image
						src="/images/emoji_md_attention.svg"
						width={60}
						height={60}
						alt="Atenção"
					/>

					<Text family="medium" weight="bold" size="v_22" color="gray_700">
						{responseMsg ? responseMsg : ' Houve um erro em sua solicitação'}
					</Text>
					<Button size="normal" variant="primary" onClick={backToLogin}>
						Voltar ao início
					</Button>
				</Box>
			);
		};

		const renderReset = () => {
			return (
				<Box
					className={[styles.boxLogin, styles.success].join(' ')}
					data-type="restrain-style">
					<Image
						src="/images/emoji_md_attention.svg"
						width={60}
						height={60}
						alt="Atenção"
					/>

					<Text weight="bold" size="v_22" color="gray_700">
						Esqueceu sua senha?
					</Text>
					<Text weight="700" size="v_18" color="gray_700">
						Enviamos um link para redefinição de senha para o seu email e
						telefone:
						<br />
					</Text>

					<Text weight="bold" size="v_18" color="black">
						{responseEmail}
						<br />
						{responsePhone}
						<br />
						<br />
					</Text>

					<Button size="normal" variant="primary" onClick={backToLogin}>
						Voltar ao início
					</Button>
				</Box>
			);
		};

		return (
			<Layout showModal={showModal}>
				<Container className={styles.container}>
					<Row>
						<Col md={12} xl={12} className={styles.welcomeMobile}>
							<Image
								src={logo?.custom}
								width={logo?.width}
								height={logo?.height}
								alt={name?.standard}
							/>
						</Col>
					</Row>
					<Row>
						<Col md={6} xl={7} className={styles.welcome}>
							<div className={styles.box}>
								<Image
									className={styles.logoDesktop}
									src={logo?.custom}
									width={logo?.width}
									height={logo?.height}
									alt={logo?.standard}
								/>

								<Text
									color="white"
									weight="v_400"
									size="v_30"
									className={styles.label}>
									Portal do{' '}
									<strong style={{ fontWeight: '600' }}>Crédito</strong>
								</Text>
								<div className={styles.request}>
									<Text
										color="white"
										weight="v_500"
										size="v_24"
										className={styles.text}>
										Acompanhe seus empréstimos com desconto em folha e faça
										novas solicitações de crédito.
									</Text>
								</div>
							</div>

							<div className={styles.img}>
								<Image
									src="/images/img-consignado.png"
									width="505"
									height="655"
									alt="Consignado"
								/>
							</div>
						</Col>
						<Col md={6} xl={5}>
							{responseStatus == '' && renderDefault()}
							{responseStatus == status.error && renderError()}
							{responseStatus == status.reset && renderReset()}
						</Col>
					</Row>
				</Container>
			</Layout>
		);
	}),
);
